// src/pages/coach/fees/FeePlans.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../api";
import UserStatusBadge from "../../../components/common/UserStatusBadge";
import ListPageLayout, {
    ListPageHeader,
    ListLoadingSpinner,
    ListTableCard,
} from "../../../components/layout/ListPageLayout";
import PaginationFooter from "../../../components/common/PaginationFooter";
import SortableTableHeader from "../../../components/common/SortableTableHeader";
import useSortedClientPagination from "../../../hooks/useSortedClientPagination";
import { feePlanSortColumns } from "../../../config/tableSortColumns";
import CarryForwardBadge from "../../../components/fees/CarryForwardBadge";
import { formatCurrency } from "../../../utils/currency";
import { branchDisplayName } from '../../../utils/displayNames';

export default function CoachFeePlans() {
    const { t } = useTranslation();
    const [branches, setBranches] = useState([]);
    const [branchId, setBranchId] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const branchName = useMemo(() => {
        const m = {};
        for (const b of branches) m[b.id] = b.name || branchDisplayName(null, b.id, t);
        return m;
    }, [branches, t]);

    useEffect(() => {
        let alive = true;
        (async () => {
            if (alive) {
                setLoading(true);
                setError("");
            }
            try {
                const bres = await API.get("/coach/branches");
                const blist = bres?.data ?? [];
                const active = Array.isArray(blist) ? blist.filter(b => b.active !== false) : [];
                if (alive) setBranches(active);

                const pres = await API.get("/coach/fee-plans", { params: branchId ? { branchId } : {} });
                if (alive) {
                    setRows(pres?.data ?? []);
                    setError("");
                }
            } catch (e) {
                console.error(e);
                if (alive) {
                    setRows([]);
                    setError(t('pages.feePlans.loadFailed'));
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [branchId]);

    const pagination = useSortedClientPagination(rows, {
        columns: feePlanSortColumns,
        resetKey: branchId,
    });

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.feePlans.title')}
                subtitle={t('pages.feePlans.subtitle')}
                actions={(
                    <select className="input max-w-xs" value={branchId} onChange={e => setBranchId(e.target.value)}>
                        <option value="">{t('pages.feePlans.allBranches')}</option>
                        {branches.map(b => <option key={b.id} value={b.id}>{b.name} (#{b.id})</option>)}
                    </select>
                )}
            />

            <ListTableCard>
                {error && <div className="banner-danger m-4">{error}</div>}
                {loading ? (
                    <ListLoadingSpinner />
                ) : (
                    <table className="min-w-full">
                        <thead className="table-header">
                            <tr>
                                <SortableTableHeader label={t('table.branch')} columnKey="branch" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <SortableTableHeader label={t('common.code')} columnKey="code" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <SortableTableHeader label={t('table.planType')} columnKey="planType" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <SortableTableHeader label={t('table.groupType')} columnKey="groupType" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <SortableTableHeader label={t('table.defaultRate')} columnKey="rate" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <SortableTableHeader label={t('table.defaultCredits')} columnKey="credits" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <th className="table-header-cell">{t('table.levelRange')}</th>
                                <th className="table-header-cell">{t('table.carryForward')}</th>
                                <th className="table-header-cell">{t('table.creditExpiry')}</th>
                                <SortableTableHeader label={t('common.active')} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {pagination.pageItems.map((r) => (
                                <tr key={r.planId} className="table-row">
                                    <td className="table-cell font-medium text-gray-900">
                                        {r.branchId ? `${branchName[r.branchId] || `#${r.branchId}`}` : '—'}
                                    </td>
                                    <td className="table-cell">
                                        <span className="badge-primary">{r.code}</span>
                                    </td>
                                    <td className="table-cell text-gray-700">{t(`pages.feePlanForm.planTypes.${r.planType}`, { defaultValue: r.planType })}</td>
                                    <td className="table-cell text-gray-700">{t(`pages.feePlanForm.groupTypes.${r.groupType}`, { defaultValue: r.groupType })}</td>
                                    <td className="table-cell font-medium">{r.defaultRate != null ? formatCurrency(r.defaultRate) : '-'}</td>
                                    <td className="table-cell">{r.defaultCredits ?? '-'}</td>
                                    <td className="table-cell">
                                        {r.levelMin ? `L${r.levelMin}–L${r.levelMax}` : '-'}
                                    </td>
                                    <td className="table-cell">
                                        <CarryForwardBadge allowed={Boolean(r.carryForwardAllowed)} docsUrl="/docs/fees#carry-forward" />
                                    </td>
                                    <td className="table-cell">{r.creditExpiryDays ?? '-'}</td>
                                    <td className="table-cell">
                                        <UserStatusBadge active={r.active} />
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && !error && (
                                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">{t('pages.feePlans.empty')}</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
                {!loading && pagination.total > 0 && (
                    <PaginationFooter
                        page={pagination.page}
                        size={pagination.size}
                        total={pagination.total}
                        onPageChange={pagination.setPage}
                        onSizeChange={pagination.setSize}
                    />
                )}
            </ListTableCard>
        </ListPageLayout>
    );
}
